import { Format, NumberFormatter, PercentageFormatter } from '@zervant/reactcomponents'
import { CharacterLimitInterface, DocumentTypes } from 'client/Interfaces/Draft/DraftInterface'
import { ContactType, ExternalTaxRatesInterface } from 'client/Interfaces/ExternalVATRatesInterface'
import { l, tsxl } from 'client/Other/Translation'
import * as React from 'react'

import { InvoiceItemType } from '../../Interfaces/Draft/DraftItemType'
import { DraftFieldsInterface } from '../../Interfaces/Draft/FieldsInterface'
import { LineInterface, LineOrder } from '../../Interfaces/Draft/LineInterface'
import { ProductTypesEnum } from '../../Interfaces/ProductInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'
import { Immutable } from '../../Other/Immutable'
import { Type } from '../../Other/Type'
import { InvoiceLine } from './InvoiceLine'
import { SubtotalLine } from './SubtotalLine'
import { InvoiceTextLine } from './TextLine'
import { InvoiceTitleLine } from './TitleLine'

export interface InvoiceLinesInterface {
  lines?: InvoiceItemType[]
}

export interface InvoiceLinesPropsInterface extends InvoiceLinesInterface {
  document: DocumentTypes
  columns?: Array<keyof LineInterface>
  settings?: SettingsInterface
  onChange: (lines: InvoiceItemType[], action: string, fieldName?: string, index?: number) => void
  fields?: DraftFieldsInterface
  locale: string
  readOnly?: boolean
  products?: any[]
  currencySymbol: string
  openProductModal: (index) => void
  openDefaultProductTypeModal?: (index, onClose) => void
  openVATOptions: (afterCreateNew) => void
  openUnitOptions: (afterCreateNew) => void
  eInvoiceSending: boolean
  eInvoiceCharacterLimit: CharacterLimitInterface
  invalidFields?: string[]
  defaultProductType: ProductTypesEnum
  externalVATRates: ExternalTaxRatesInterface
  isAgerasNextMigration?: boolean
  customerCountry?: string | null
  customerType?: ContactType | null
}

export class InvoiceItems extends React.Component<InvoiceLinesPropsInterface, InvoiceLinesInterface> {
  constructor(props) {
    super(props)

    this.lines = Immutable.copy(props.lines)
    this.setFormatters(props.currencySymbol)
  }

  private lines: InvoiceItemType[]
  private discountFormatter: NumberFormatter
  private currencyFormatter: NumberFormatter
  private isProductTypeModalOpen = false

  public static defaultProps: InvoiceLinesPropsInterface = {
    lines: [],
    onChange: () => {},
    locale: 'en_GB',
  }

  private dropLine(at, which): void {
    const b = this.lines.splice(which, 1)
    if (at >= this.lines.length) {
      this.lines.push(b[0])
    } else {
      const a = this.lines.splice(at, 1, b[0])
      this.lines.splice(at + 1, 0, a[0])
    }
  }

  private triggerChange(action: string, index?: number, fieldName?: string): void {
    this.props.onChange(this.lines, action, fieldName, index)
  }

  public UNSAFE_componentWillReceiveProps(props) {
    this.setFormatters(props.currencySymbol)
  }

  private setFormatters(currency) {
    if (this.props.fields.discountType.value === 'amount') {
      this.discountFormatter = new NumberFormatter(Format.fromLocale(this.props.locale), currency)
    } else {
      this.discountFormatter = new PercentageFormatter(Format.fromLocale(this.props.locale), true)
    }
    this.currencyFormatter = new NumberFormatter(Format.fromLocale(this.props.locale), currency)
  }

  private columnsLength() {
    return this.props.columns.length - (~this.props.columns.indexOf('index') ? 1 : 0)
  }

  private getProductTypeFromSettings() {
    const invoiceSettings = this.props.settings.models.find((model) => model.name === 'models.invoice.sales')

    if (!invoiceSettings || !invoiceSettings.fields || !Array.isArray(invoiceSettings.fields)) {
      return
    }

    const productTypeSetting = invoiceSettings.fields.find((field) => field.name === 'productType')

    return productTypeSetting && productTypeSetting.default
  }

  private onCloseProductTypeModal() {
    this.isProductTypeModalOpen = false
  }

  private openDefaultProductTypeModal(index) {
    if (this.isProductTypeModalOpen) {
      return
    }

    if (!this.props.defaultProductType && this.props.openDefaultProductTypeModal) {
      this.props.openDefaultProductTypeModal(index, () => this.onCloseProductTypeModal())
      this.isProductTypeModalOpen = true
    }
  }

  public render(): JSX.Element {
    const self = this
    self.lines = Immutable.copy(this.props.lines)
    const lines: JSX.Element[] = []
    const { customerCountry, isAgerasNextMigration, fields } = this.props
    const invoiceTypeId = fields?.type?.value

    this.props.lines.forEach((line, key) => {
      if (Type.isInvoiceLine(line)) {
        lines.push(
          <InvoiceLine
            settings={this.props.settings}
            id={'' + key}
            document={this.props.document}
            index={key}
            key={key}
            automation={'invoiceItem-' + key}
            invalidFields={this.props.invalidFields}
            {...line}
            products={self.props.products}
            columns={this.props.columns}
            discountFormat={this.discountFormatter}
            currencyFormat={this.currencyFormatter}
            locale={this.props.locale}
            onDrop={(id) => {
              self.dropLine(key, id)
              self.triggerChange('drop', key)
            }}
            onDelete={() => {
              self.lines.splice(key, 1)
              self.triggerChange('delete', key)
            }}
            onChange={(value, fieldName) => {
              self.lines[key] = value
              self.triggerChange('change', key, fieldName)
            }}
            defaultProductType={this.props.defaultProductType}
            readOnly={this.props.readOnly}
            openProductModal={(index) => this.props.openProductModal(index)}
            openDefaultProductTypeModal={(index) => this.openDefaultProductTypeModal(index)}
            openVATOptions={this.props.openVATOptions}
            openUnitOptions={this.props.openUnitOptions}
            isAgerasNextMigration={isAgerasNextMigration}
            customerCountry={customerCountry}
            customerType={this.props.customerType}
            externalVATRates={this.props.externalVATRates}
            invoiceTypeId={invoiceTypeId}
          />,
        )
      } else if (Type.isInvoiceSubtotalLine(line)) {
        lines.push(
          <SubtotalLine
            key={key}
            currencyFormat={this.currencyFormatter}
            automation={`invoiceItem-Subtotal-${key}`}
            grossTotal={line.grossTotal}
            taxTotal={line.taxTotal}
            netTotal={line.netTotal}
            showNetTotal={this.props.columns.indexOf('netTotal') >= 0}
            showTaxTotal={this.props.columns.indexOf('taxTotal') >= 0}
            showGrossTotal={this.props.columns.indexOf('grossTotal') >= 0}
            showProductType={
              (this.props.document === 'creditnote' || this.props.document === 'invoice') &&
              this.props.columns.includes('productType')
            }
            type="subtotal"
            columns={this.columnsLength()}
            title={l('invoice.subtotalTitle')}
            id={key}
            onDelete={() => {
              self.lines.splice(key, 1)
              self.triggerChange('delete', key)
            }}
            onDrop={(id) => {
              self.dropLine(key, id)
              self.triggerChange('drop', key)
            }}
            readonly={this.props.readOnly}
          />,
        )
      } else if (Type.isInvoiceTitleLine(line)) {
        lines.push(
          <InvoiceTitleLine
            columns={this.columnsLength()}
            id={key}
            key={key}
            automation={'invoiceItem-' + line.type + '-' + key}
            type={line.type}
            content={line.content}
            onDrop={(id) => {
              self.dropLine(key, id)
              self.triggerChange('drop', key)
            }}
            onChange={(value, fieldName) => {
              self.lines[key] = value
              self.triggerChange('change', key, fieldName)
            }}
            onDelete={() => {
              self.lines.splice(key, 1)
              self.triggerChange('delete', key)
            }}
            readonly={this.props.readOnly}
            placeholder={l('invoice.line.title')}
            showProductType={
              (this.props.document === 'creditnote' || this.props.document === 'invoice') &&
              this.props.columns.includes('productType')
            }
          />,
        )
      } else {
        const isInvalid =
          (this.props.eInvoiceCharacterLimit?.textFields && line.eInvoiceLimit && this.props.eInvoiceSending) || false
        lines.push(
          <InvoiceTextLine
            columns={this.columnsLength()}
            id={key}
            key={key}
            automation={'invoiceItem-' + line.type + '-' + key}
            type={line.type}
            content={line.content}
            onDrop={(id) => {
              self.dropLine(key, id)
              self.triggerChange('drop', key)
            }}
            onChange={(value, fieldName) => {
              self.lines[key] = value
              self.triggerChange('change', key, fieldName)
            }}
            onDelete={() => {
              self.lines.splice(key, 1)
              self.triggerChange('delete', key)
            }}
            readonly={this.props.readOnly}
            invalid={isInvalid}
            showProductType={
              (this.props.document === 'creditnote' || this.props.document === 'invoice') &&
              this.props.columns.includes('productType')
            }
          />,
        )
      }
    })

    const th = []
    let columnName
    LineOrder.forEach((name) => {
      if (name === 'productType' && this.props.document === DocumentTypes.ESTIMATE) {
        return null
      }
      columnName = name
      if (name === 'grossTotal') {
        columnName = 'total'
      }
      if (this.props.columns.indexOf(name) > -1) {
        th.push(
          <th key={name} className={name}>
            {tsxl('invoice.line.' + columnName)}
          </th>,
        )
      }
    })

    return (
      <div>
        <hr />
        <table className="InvoiceItems">
          <thead>
            <tr>
              <th className="drag"></th>
              {th}
              <th className="remove"></th>
            </tr>
          </thead>
          <tbody>{lines}</tbody>
        </table>
      </div>
    )
  }
}
